import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, normalizeStyle as _normalizeStyle, withCtx as _withCtx, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, mergeProps as _mergeProps, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "row" }
const _hoisted_2 = { class: "flex xs12" }
const _hoisted_3 = { class: "row" }
const _hoisted_4 = { class: "flex xs12" }

import { AgentService } from "@/codes/services/AgentService";
import { ReactiveData } from "@/codes/types/MDOTypes";
import { onMounted, reactive, ref } from "vue";
import { useI18n } from "vue-i18n";
import { DEFAULT_ITEMS_PER_PAGE, ROUTE_NAMES, VUEX_ACTIONS } from '@/codes/util/constants';
import { calculatePageCount, hasWritePermission } from '@/codes/util/functions';
import { Agent } from "@/codes/gen/types";
import { Confirm } from "@/codes/util/swalUtil";
import store from "@/store";
import { useForm } from "vee-validate";


export default /*#__PURE__*/_defineComponent({
  setup(__props) {

const createEmptyAgent = function(): Agent {
  return { enable: true } as Agent;
}

const loadingAgents = ref(false);
let page = 1;
const pageCount = ref(1);
const itemsPerPage = ref(DEFAULT_ITEMS_PER_PAGE);
const agents = reactive<ReactiveData<Agent[]>>({ data: [] as Agent[] });
const agent = reactive<ReactiveData<Agent>>({ data: createEmptyAgent() })
const action = ref<"newAgent"|"editAgent">("newAgent");
const showForm = ref(false);

const form = useForm();
const formMeta = reactive(form.meta);

let pageLoaded = 0;

const i18n = useI18n();

const headers = reactive({ data: [
  { key: 'name', label: i18n.t('register.name') }
] as any[] });

const checkHasWritePermission = function() {
  if (hasWritePermission(ROUTE_NAMES.agents)) {
    headers.data.push({ key: 'actions', label: i18n.t('form.actions'), width: 75 });
  }
}

const loadAgents = async function() {
  agents.data = [];
  loadingAgents.value = true;
  const service = new AgentService();
  const agentsEdge = await service.getAll({ page: page, perPage: itemsPerPage.value });
  if (agentsEdge) {
    pageCount.value = calculatePageCount(itemsPerPage.value, agentsEdge.pagination?.total ?? 1);
    agents.data = agentsEdge.edges;
    pageLoaded = page;
  }
  loadingAgents.value = false;
}

const onPageChanged = function(newPage: number) {
  page = newPage;
  loadAgents();
}

onMounted(() => {
  loadAgents();

  store.subscribeAction({
    after: (action) => {
      if (action.type === VUEX_ACTIONS.setUserInfo) {
        checkHasWritePermission();
      }
    }
  });

  checkHasWritePermission();
});

const doNew = function() {
  action.value = "newAgent";
  showForm.value = true;
}

const edit = function(toEdit: Agent) {
  action.value = "editAgent";
  showForm.value = true;
  agent.data = { id: toEdit.id, name: toEdit.name, enable: toEdit.enable } as Agent;
}

const validate = async function() {
  const v = await form.validate();
  if (!v.valid) {
    return false;
  }
  return true;
}

const cancel = function() {
  if (!formMeta.value.dirty) {
    Confirm.fire({
      title: i18n.t('form.confirmCancelTitle'),
      text: i18n.t('form.confirmCancelText')
    })
    .then((result) => {
      if (result.isConfirmed) {
        agent.data = createEmptyAgent();
        showForm.value = false;
      }
    })
  }
}

const save = async function() {
  if (await validate()) {
    const service = new AgentService();
    if (action.value === "newAgent") {
      const createResult = await service.create({ name: agent.data.name, enable: agent.data.enable });
      if (createResult) {
        if (agents.data.length < DEFAULT_ITEMS_PER_PAGE) {
          agents.data = [ ...agents.data, createResult.data ];
        } else {
          onPageChanged(pageLoaded + 1);
        }
        agent.data = createEmptyAgent();
        showForm.value = false;
      }
    } else {
      const updateResult = await service.update(agent.data.id, { name: agent.data.name, enable: agent.data.enable });
      if (updateResult) {
        const agentEdit = agents.data.filter((u) => u.id === updateResult.data.id);
        if (agentEdit && agentEdit.length > 0) {
          const index = agents.data.indexOf(agentEdit[0]);
          const newList = [ ...agents.data ];
          newList.splice(index, 1, updateResult.data);
          agents.data = newList;
          agent.data = createEmptyAgent();
          showForm.value = false;
        }
      }
    }
  }
}

const remove = function(agent: Agent) {
  Confirm.fire({
    title: i18n.t('form.confirmDeleteTitle'),
    text: i18n.t('form.confirmDeleteMessage', { register: i18n.t('agent.describe', { agent: agent.name }) })
  })
  .then(async (result) => {
    if (result.isConfirmed) {
      const service = new AgentService();
      const result = await service.remove(agent.id);
      if (result && result.data.id) {
        const userRemove = agents.data.filter((u) => u.id === result.data.id);
        if (userRemove && userRemove.length > 0) {
          const index = agents.data.indexOf(userRemove[0]);
          const newList = [ ...agents.data ];
          newList.splice(index, 1);
          agents.data = newList;
          if (agents.data.length === 0) {
            if (page > 1) {
              onPageChanged(1);
            } else {
              window.location.reload();
            }
          }
        }
      }
    }
  });
}


return (_ctx: any,_cache: any) => {
  const _component_va_button = _resolveComponent("va-button")!
  const _component_va_data_table = _resolveComponent("va-data-table")!
  const _component_base_list = _resolveComponent("base-list")!
  const _component_va_navbar_item = _resolveComponent("va-navbar-item")!
  const _component_va_navbar = _resolveComponent("va-navbar")!
  const _component_va_card_title = _resolveComponent("va-card-title")!
  const _component_va_input = _resolveComponent("va-input")!
  const _component_VField = _resolveComponent("VField")!
  const _component_va_switch = _resolveComponent("va-switch")!
  const _component_va_card_content = _resolveComponent("va-card-content")!
  const _component_va_card = _resolveComponent("va-card")!
  const _component_va_modal = _resolveComponent("va-modal")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", null, [
      _createVNode(_component_base_list, {
        page: _unref(page),
        "page-count": pageCount.value,
        "current-route": _unref(ROUTE_NAMES).agents,
        "page-loaded": _unref(pageLoaded),
        "show-delete": false,
        onPageChanged: onPageChanged,
        onDoNew: doNew
      }, {
        default: _withCtx(() => [
          _createVNode(_component_va_data_table, {
            items: _unref(agents).data,
            columns: _unref(headers).data,
            "sticky-header": "",
            loading: loadingAgents.value,
            "no-data-html": "Sem resultados",
            style: _normalizeStyle({
          '--va-data-table-scroll-table-color': 'var(--va-divider)',
        })
          }, {
            "cell(actions)": _withCtx(({ rowData }) => [
              _createVNode(_component_va_button, {
                icon: "fas fa-edit",
                class: "mr-1",
                color: "info",
                size: "small",
                onClick: ($event: any) => (edit(rowData))
              }, null, 8, ["onClick"]),
              _createVNode(_component_va_button, {
                icon: "fas fa-trash",
                class: "danger",
                size: "small",
                onClick: ($event: any) => (remove(rowData))
              }, null, 8, ["onClick"])
            ]),
            _: 1
          }, 8, ["items", "columns", "loading", "style"])
        ]),
        _: 1
      }, 8, ["page", "page-count", "current-route", "page-loaded"])
    ]),
    _createVNode(_component_va_modal, {
      modelValue: showForm.value,
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => (showForm.value = $event)),
      blur: "",
      "no-dismiss": "",
      "no-padding": "",
      size: "large",
      "ok-text": _ctx.$t('form.save'),
      "cancel-text": _ctx.$t('form.cancel'),
      onBeforeClose: validate,
      onCancel: cancel,
      onOk: save
    }, {
      default: _withCtx(() => [
        _createVNode(_component_va_card, null, {
          default: _withCtx(() => [
            _createVNode(_component_va_card_title, { class: "flex xs12 pa-0" }, {
              default: _withCtx(() => [
                _createVNode(_component_va_navbar, {
                  color: "info",
                  shape: "",
                  class: "mb-2"
                }, {
                  left: _withCtx(() => [
                    _createVNode(_component_va_navbar_item, null, {
                      default: _withCtx(() => [
                        _createElementVNode("h3", {
                          class: "display-3",
                          style: _normalizeStyle({
                    'color': 'var(--va-white)',
                  })
                        }, _toDisplayString(_ctx.$t(`menu.${action.value}`)), 5)
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }),
            _createVNode(_component_va_card_content, { class: "pa-2" }, {
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_1, [
                  _createElementVNode("div", _hoisted_2, [
                    _createVNode(_component_VField, {
                      name: "name",
                      rules: "required|min:6"
                    }, {
                      default: _withCtx(({ field, errors: nameErrors }) => [
                        _createVNode(_component_va_input, _mergeProps(field, {
                          modelValue: _unref(agent).data.name,
                          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (_unref(agent).data.name = $event)),
                          class: "mb-4",
                          label: _ctx.$t('register.name'),
                          error: nameErrors && nameErrors.length > 0,
                          "error-messages": nameErrors
                        }), null, 16, ["modelValue", "label", "error", "error-messages"])
                      ]),
                      _: 1
                    })
                  ])
                ]),
                _createElementVNode("div", _hoisted_3, [
                  _createElementVNode("div", _hoisted_4, [
                    _createVNode(_component_va_switch, {
                      modelValue: _unref(agent).data.enable,
                      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => (_unref(agent).data.enable = $event)),
                      "true-inner-label": _ctx.$t('form.enabled'),
                      "false-inner-label": _ctx.$t('form.disabled'),
                      class: "mr-4"
                    }, null, 8, ["modelValue", "true-inner-label", "false-inner-label"])
                  ])
                ])
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["modelValue", "ok-text", "cancel-text"])
  ], 64))
}
}

})